<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import MetaBusinessAgentAPI from 'dashboard/api/metaBusinessAgent';

import Button from 'dashboard/components-next/button/Button.vue';
import ResourceList from './ResourceList.vue';

const props = defineProps({
  hookId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const T = key => t(`INTEGRATION_SETTINGS.META_BUSINESS_AGENT.FILES.${key}`);

const files = ref([]);
const fileInput = ref(null);
const pendingDelete = ref(null);
const showDeleteModal = ref(false);

const fetchFiles = async () => {
  try {
    const { data } = await MetaBusinessAgentAPI.listFiles(props.hookId);
    files.value = data;
  } catch (error) {
    useAlert(T('API.FETCH_ERROR'));
  }
};

onMounted(fetchFiles);

const triggerUpload = () => fileInput.value.click();

const onFileSelected = async event => {
  const file = event.target.files[0];
  if (!file) return;
  try {
    await MetaBusinessAgentAPI.uploadFile(props.hookId, file);
    useAlert(T('API.UPLOAD_SUCCESS'));
    fetchFiles();
  } catch (error) {
    useAlert(T('API.UPLOAD_ERROR'));
  } finally {
    event.target.value = '';
  }
};

const confirmDelete = item => {
  pendingDelete.value = item;
  showDeleteModal.value = true;
};

const deleteFile = async () => {
  try {
    await MetaBusinessAgentAPI.deleteFile(props.hookId, pendingDelete.value.id);
    useAlert(T('API.DELETE_SUCCESS'));
    fetchFiles();
  } catch (error) {
    useAlert(T('API.DELETE_ERROR'));
  } finally {
    showDeleteModal.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex justify-end">
      <input
        ref="fileInput"
        type="file"
        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.csv,.xlsx"
        class="hidden"
        @change="onFileSelected"
      />
      <Button :label="T('UPLOAD_BUTTON')" @click="triggerUpload" />
    </div>
    <ResourceList
      :items="files"
      :empty-state="T('EMPTY_STATE')"
      @delete="confirmDelete"
    >
      <template #default="{ item }">
        <p class="font-medium text-n-slate-12 m-0">{{ item.file_name }}</p>
      </template>
    </ResourceList>

    <woot-delete-modal
      v-model:show="showDeleteModal"
      :on-close="() => (showDeleteModal = false)"
      :on-confirm="deleteFile"
      :title="T('DELETE_CONFIRM_TITLE')"
      :message="T('DELETE_CONFIRM_MESSAGE')"
      :confirm-text="$t('INTEGRATION_APPS.DELETE.CONFIRM_BUTTON_TEXT.INBOX')"
      :reject-text="$t('INTEGRATION_APPS.DELETE.CANCEL_BUTTON_TEXT')"
    />
  </div>
</template>
